import { ArrowLeft, BookOpen, Ban, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/eh8zi2sq_logo.svg";

const ContentPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="content-policy-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="content-policy-nav-logo">
              <img src={LOGO_URL} alt="GospelTube" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="content-policy-back-btn"
            >
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-xl bg-brand/20 text-brand">
              <BookOpen size={28} />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white">Content Policy & Community Guidelines</h1>
              <p className="text-gray-500 text-sm">Effective Date: January 30, 2026 | Last Updated: January 30, 2026</p>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 md:p-12 space-y-8">
              
              <p className="text-gray-400 leading-relaxed">
                GospelTube exists to promote faith, encouragement, wellbeing, and uplifting content. These rules apply to all users, creators, churches, charities, and organizations.
              </p>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
                  <CheckCircle size={24} className="text-green-400" />
                  1) What We Encourage
                </h2>
                <p className="text-gray-400 leading-relaxed">We welcome content such as:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                  <li>Church services, worship, teaching, devotionals</li>
                  <li>Music ministrations and performances</li>
                  <li>Faith-based encouragement and moral storytelling</li>
                  <li>Mental health and wellbeing support content (responsible, non-exploitative)</li>
                  <li>Charity initiatives and community outreach</li>
                  <li>Uplifting/values-based movies and films uploaded or distributed by rights holders or authorized partners</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-3">
                  <Ban size={24} className="text-red-400" />
                  2) Prohibited Content
                </h2>
                <p className="text-gray-400 leading-relaxed">
                  You may not post, upload, stream, or share content that includes or promotes:
                </p>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">a) Illegal activity or harm</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Illegal acts, instructions for wrongdoing, credible threats, or incitement to violence</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">b) Hate, harassment, and bullying</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Hate speech, slurs, dehumanization, or content targeting protected groups</li>
                  <li>Harassment, threats, stalking, doxxing, or targeted abuse</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">c) Sexual exploitation and explicit sexual content</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Any sexual content involving minors (zero tolerance)</li>
                  <li>Pornography, sexual exploitation, or coercion</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">d) Graphic violence</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Gore, graphic injury, or content intended to shock</li>
                  <li>Praise or support of violent extremist acts</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">e) Self-harm encouragement</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Content encouraging self-harm or suicide is prohibited</li>
                  <li>Supportive awareness/recovery content may be allowed with safeguards and may be age-gated or limited</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">f) Scams, deception, and fraud</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Phishing, impersonation, fake giveaways, deceptive fundraising, or misrepresentation</li>
                  <li>Misrepresentation of a church/charity/organization</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">g) Spam and platform abuse</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Spam, repetitive posting, engagement manipulation, or malware</li>
                </ul>

                <h3 className="text-xl font-medium text-white mt-6 mb-3">h) Intellectual property violations</h3>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li>Pirated movies, music, sermons, broadcasts, or unauthorized reuploads</li>
                  <li>Content that violates copyrights, trademarks, or publicity rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">3) Fundraising / Donations Integrity</h2>
                <p className="text-gray-400 leading-relaxed">If you accept donations/tips through the Service:</p>
                <ul className="list-disc list-inside text-gray-400 space-y-2 mt-2">
                  <li>You must not mislead donors about identity, purpose, or use of funds</li>
                  <li>We may request verification, documentation, or proof of authorization</li>
                  <li>We may pause or restrict fundraising features to protect users and comply with law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">4) Moderation Approach</h2>
                <p className="text-gray-400 leading-relaxed">
                  We use post-moderation (removal after reports and/or detection), and we may proactively remove content when we become aware of violations. We may restrict distribution, add warnings, age-gate, suspend, or terminate accounts.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">5) Reporting and Appeals</h2>
                <ul className="list-disc list-inside text-gray-400 space-y-2">
                  <li><strong className="text-white">To report content:</strong> <a href="mailto:info@gospeltube.tv" className="text-brand hover:underline">info@gospeltube.tv</a> (or in-app reporting where available)</li>
                  <li><strong className="text-white">Appeals:</strong> send the content link/ID, your account email, and why you believe it complies</li>
                </ul>
              </section>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} GospelTube Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mt-4 text-sm">
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/content-policy" className="text-brand">Content Policy</Link>
            <Link to="/dmca" className="text-gray-400 hover:text-white transition-colors">Copyright/DMCA</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContentPolicyPage;
